
import { Shield, CheckCircle, BarChart3, Target, Lock, Scale } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const NistCsf = () => {
  return (
    <LandingPageTemplate
      title="NIST CSF Implementation"
      description="Expert guidance in implementing the NIST Cybersecurity Framework to strengthen your organization's security posture."
      keywords={[
        "NIST CSF",
        "cybersecurity framework",
        "security implementation",
        "risk management",
        "security assessment"
      ]}
      canonicalUrl="/cybersecurity/nist-csf/"
      schemaType="service"
      schemaData={{
        name: "NIST CSF Implementation Services",
        description: "Professional NIST Cybersecurity Framework implementation",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Implement"
        highlighted="NIST CSF"
        description="Strengthen your security posture with expert implementation of the NIST Cybersecurity Framework."
        ctaText="Get Started"
        heroPattern="roadmap"
      />

      <FeatureSection 
        title="Comprehensive Implementation"
        subtitle="KEY FEATURES"
        description="Our NIST CSF implementation services cover all framework core functions."
        features={[
          {
            title: "Identify",
            description: "Asset management and risk assessment.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Protect",
            description: "Security control implementation.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Detect",
            description: "Continuous monitoring capabilities.",
            icon: <Target className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Respond",
            description: "Incident response planning.",
            icon: <CheckCircle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Recover",
            description: "Business continuity management.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Measure",
            description: "Security metrics and reporting.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Implementation Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to implementing the NIST CSF"
        steps={[
          {
            title: "Assessment",
            description: "Evaluate current security posture"
          },
          {
            title: "Planning",
            description: "Develop implementation roadmap"
          },
          {
            title: "Implementation",
            description: "Execute framework controls"
          },
          {
            title: "Integration",
            description: "Integrate with existing processes"
          },
          {
            title: "Measurement",
            description: "Monitor and measure effectiveness"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our NIST CSF Services"
        benefits={[
          {
            title: "Expert Guidance",
            description: "Certified NIST framework specialists"
          },
          {
            title: "Comprehensive Coverage",
            description: "All framework core functions"
          },
          {
            title: "Clear Roadmap",
            description: "Structured implementation plan"
          },
          {
            title: "Risk Reduction",
            description: "Enhanced security posture"
          },
          {
            title: "Compliance Support",
            description: "Meet regulatory requirements"
          },
          {
            title: "Continuous Improvement",
            description: "Ongoing optimization"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Implement NIST CSF?"
        description="Start your journey toward a stronger security posture with NIST CSF."
        ctaText="Schedule Consultation"
        background="purple"
        modalTitle="Request NIST CSF Implementation"
        modalDescription="Tell us about your NIST CSF implementation needs."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "NIST CSF",
          service_interest: "Security Framework"
        }}
      />
    </LandingPageTemplate>
  );
};

export default NistCsf;
